import React, { useEffect, useRef, useState } from "react";
import { animate } from "animejs";
import { createTimeline } from "animejs/timeline";
import { useDrawLoop } from "./utils";
import "./styles.css";

export const handler = ({ inputs, mechanic }) => {
  const {
    format,
    customWidth,
    customHeight,
    text,
    textColor,
    textSize,
    textFieldWidth,
    rectangleColor,
    circleSize,
    circleColor,
    backgroundColor,
    duration
  } = inputs;

  // Calculate dimensions based on format
  const formatDimensions = {
    vertical: { width: 1080, height: 1350 },
    square: { width: 1080, height: 1080 },
    landscape: { width: 1080, height: 566 },
    story: { width: 1080, height: 1920 },
    custom: { width: customWidth, height: customHeight }
  };
  
  const { width, height } = formatDimensions[format];
  
  const startTime = useRef(Date.now());
  const isPlaying = useRef(true);
  const frameCount = useDrawLoop(isPlaying.current);
  const timelineRef = useRef(null);
  const circleRef = useRef(null);
  const scaleTrackerRef = useRef({ value: 1 });
  const [currentScale, setCurrentScale] = useState(1);
  
  // Calculate effective circle size with animation scale
  const effectiveCircleSize = circleSize * currentScale;
  
  // Rectangle dimensions (top-left, until circle and text)
  const rectWidth = width - textFieldWidth;
  const rectHeight = height - effectiveCircleSize;
  
  // Split text into characters for vertical stacking
  const characters = text.split('');

  // Initialize anime.js timeline once
  useEffect(() => {
    if (!timelineRef.current && circleRef.current) {
      const tl = createTimeline({
        defaults: {
          duration: duration * 1000,
          ease: 'inOutQuad'
        },
        autoplay: false
      });

      // Animate both the visual transform and the scale tracker
      tl.add(circleRef.current, {
        scale: { to: [1, 1.3, 0.8, 1.1, 1] }
      }, 0);
      
      tl.add(scaleTrackerRef.current, {
        value: { to: [1, 1.3, 0.8, 1.1, 1] }
      }, 0);

      timelineRef.current = tl;
    }
  }, [duration]);

  // Update animation progress
  useEffect(() => {
    if (timelineRef.current) {
      const elapsed = Date.now() - startTime.current;
      
      timelineRef.current.seek(elapsed);
      
      // Update scale state from the animated value
      setCurrentScale(scaleTrackerRef.current.value);

      if (elapsed < duration * 1000) {
        mechanic.frame();
      } else if (isPlaying.current) {
        isPlaying.current = false;
        mechanic.done();
      }
    }
  }, [frameCount, duration]);

  return (
    <div
      className="poster-layout-container"
      style={{
        width: `${width}px`,
        height: `${height}px`,
        backgroundColor: backgroundColor,
        position: "relative",
        overflow: "hidden"
      }}
    >
      {/* Rectangle - top left */}
      <div
        style={{
          position: "absolute",
          width: `${rectWidth}px`,
          height: `${rectHeight}px`,
          left: 0,
          top: 0,
          backgroundColor: rectangleColor
        }}
      />
      
      {/* Circle - bottom left */}
      <div
        ref={circleRef}
        style={{
          position: "absolute",
          width: `${effectiveCircleSize}px`,
          height: `${effectiveCircleSize}px`,
          left: 0,
          top: `${height - effectiveCircleSize}px`,
          backgroundColor: circleColor,
          borderRadius: "50%",
          transformOrigin: "left bottom"
        }}
      />
      
      {/* Text field - right side, full height */}
      <div
        style={{
          position: "absolute",
          width: `${textFieldWidth}px`,
          height: `${height}px`,
          left: `${width - textFieldWidth}px`,
          top: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "Figtree, sans-serif",
          fontWeight: 700,
          fontSize: `${textSize}px`,
          lineHeight: `${textSize * 1.2}px`,
          color: textColor,
          textTransform: "lowercase",
          letterSpacing: "-0.02em"
        }}
      >
        {characters.map((char, index) => (
          <div key={index}>{char}</div>
        ))}
      </div>
    </div>
  );
};

export const inputs = {
  format: {
    type: "text",
    default: "vertical",
    options: ["vertical", "square", "landscape", "story", "custom"],
    label: "Format"
  },
  customWidth: {
    type: "number",
    default: 1080,
    label: "Width",
    editable: (inputs) => inputs.format === "custom",
    validation: () => null
  },
  customHeight: {
    type: "number",
    default: 1350,
    label: "Height",
    editable: (inputs) => inputs.format === "custom",
    validation: () => null
  },
  text: {
    type: "text",
    default: "garibaldi"
  },
  textColor: {
    type: "color",
    model: "hex",
    default: "#000000",
    label: "Text Color"
  },
  textSize: {
    type: "number",
    default: 250,
    min: 50,
    max: 500,
    step: 5,
    slider: true,
    label: "Text Size (px)"
  },
  textFieldWidth: {
    type: "number",
    default: 261,
    min: 100,
    max: 500,
    step: 10,
    slider: true,
    label: "Text Field Width (px)"
  },
  rectangleColor: {
    type: "color",
    model: "hex",
    default: "#D9D9D9",
    label: "Rectangle Color"
  },
  circleColor: {
    type: "color",
    model: "hex",
    default: "#D9D9D9",
    label: "Circle Color"
  },
  circleSize: {
    type: "number",
    default: 501,
    min: 100,
    max: 1000,
    step: 10,
    slider: true,
    label: "Circle Size (px)"
  },
  backgroundColor: {
    type: "color",
    model: "hex",
    default: "#FFFFFF",
    label: "Background Color"
  },
  duration: {
    type: "number",
    default: 3,
    min: 1,
    max: 10,
    slider: true,
    label: "Duration (seconds)"
  }
};

export const presets = {
  vertical: {
    format: "vertical"
  },
  square: {
    format: "square"
  },
  landscape: {
    format: "landscape"
  },
  story: {
    format: "story"
  }
};

export const settings = {
  engine: require("@mechanic-design/engine-react"),
  name: "Poster Layout",
  animated: true,
  hideFunction: true
};
